import { DEFAULT_DESTINATION_EXPANSIONS } from '@eventbrite/event-renderer';
import { sdkRequest } from '@eventbrite/http';
import { useInfiniteQuery, useQueryClient } from '@tanstack/react-query';
import React from 'react';
import { BucketsContent, PaginatedBucketData } from '../../../../types';
import { BrowseDataContext } from '../../context';
import { transformBuckets } from './utils';
const fetchBrowseBuckets = ({ queryKey, pageParam }: any) => {
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    const [_, placeId, isOnline] = queryKey;
    const { excludedBuckets } = pageParam;

    return sdkRequest('/api/v3/destination/city-browse/', {
        method: 'POST',
        body: JSON.stringify({
            place_id: placeId,
            online_events_only: isOnline,
            buckets_to_exclude: excludedBuckets,
            'expand.destination_event': DEFAULT_DESTINATION_EXPANSIONS,
        }),
    }).then(
        (res: {
            buckets: BucketsContent;
            excluded_buckets: BucketsContent;
            more_buckets: BucketsContent;
        }) => {
            return {
                buckets: transformBuckets({
                    buckets: res.buckets,
                    expanded: true,
                }),
                excludedBuckets: res.excluded_buckets,
                moreBuckets: res.more_buckets,
            };
        },
    );
};

type BrowseDataLayerData = {
    pages: PaginatedBucketData[];
    pageParams: any[];
};

export const MainContentDataLayer = (props: {
    buckets: BucketsContent;
    excludedBuckets?: BucketsContent;
    moreBuckets?: BucketsContent;
    children?: React.ReactNode;
    placeId?: string | null;
    isOnline?: boolean;
}) => {
    const { children, placeId, isOnline } = props;
    const queryClient = useQueryClient();
    const queryKey = ['browse-buckets', placeId, isOnline];
    const { data, fetchNextPage, hasNextPage } = useInfiniteQuery(
        queryKey,
        fetchBrowseBuckets as any,
        {
            getNextPageParam: (prevPage) => {
                if (prevPage.moreBuckets) {
                    return {
                        excludedBuckets: prevPage.excludedBuckets,
                    };
                }
            },
            initialData: {
                pages: [
                    {
                        buckets: transformBuckets({
                            buckets: props.buckets,
                            expanded: false,
                        }),
                        excludedBuckets: props.excludedBuckets,
                        moreBuckets: props.moreBuckets,
                    },
                ],
                pageParams: [{ excludedBuckets: props.excludedBuckets }],
            },
        },
    );

    const removeEvent = (bucketKey: string, eventId: string) => {
        queryClient.setQueryData(queryKey, (oldData?: BrowseDataLayerData) => {
            if (!oldData) return;

            const newBucketPages = oldData.pages.map(
                ({ buckets, ...otherData }) => {
                    const newBuckets = buckets.map((bucket: any) => {
                        if (bucket.key === bucketKey) {
                            return {
                                ...bucket,
                                content: bucket.content.filter(
                                    (event: any) => event.id !== eventId,
                                ),
                            };
                        }
                        return bucket;
                    });
                    return {
                        buckets: newBuckets,
                        ...otherData,
                    };
                },
            );

            return {
                ...oldData,
                pages: newBucketPages,
            };
        });
    };

    return (
        <BrowseDataContext.Provider
            value={{
                fetchNextPage,
                hasNextPage,
                removeEvent,
                buckets: data?.pages.reduce(
                    (memo: any, page) => [...memo, ...page.buckets],
                    [],
                ),
            }}
        >
            {children}
        </BrowseDataContext.Provider>
    );
};
